import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "AuthContextWrapper"
import { OUTCOMES } from "../../../constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { RenderFileUploadBox } from "../../fileCabinet/FileUploadArea";
import { documentsAPI } from "apis";
import { CheckCircle } from "@mui/icons-material";
import { DOC_TYPE_ID_DEATH_CERTIFICATE, DOC_TYPE_ID_LETTERS_TESTAMENTARY, DOC_TYPE_ID_PHOTO_VERIFICATION, DOC_TYPE_ID_WILL, useFileCabinetContext } from "FileCabinetContextWrapper";
import { Loading } from "views/shared/Loading";
import { EXECUTOR_VERIFICATION_STEP_DEATH_CERTIFICATE, EXECUTOR_VERIFICATION_STEP_LETTERS_OR_AGREEMENT, EXECUTOR_VERIFICATION_STEP_PHOTO_VERIFIATION_ID, EXECUTOR_VERIFICATION_STEP_WILL, EXECUTOR_VERIFICATION_SUBMITTED_ID, ExecutorVerificationSteps, findMostRecentDoc } from ".";
import { ExecutorVerificationContext } from "ExecutorVerificationContextWrapper";
import { getDocumentContents } from "apis/getDocumentContents";


const stepInfo: { [key: string]: any } = {
  [EXECUTOR_VERIFICATION_STEP_WILL.toString()]: {
    image: "/static/images/Will_Sample.svg",
    documentName: "Copy of Will",
    documentTypeId: DOC_TYPE_ID_WILL,
    title: "Please upload a copy of the latest valid will or codicil",
    subtitle: "Also referred to as the “last will and testament,” this legal document outlines how to distribute assets of the deceased after their passing. A codicil is a legal document that outlines any additions or modifications to the last will and testament.",
  },
  [EXECUTOR_VERIFICATION_STEP_DEATH_CERTIFICATE.toString()]: {
    image: "/static/images/Death_Cert_Sample.png",
    documentName: "Death Certificate",
    documentTypeId: DOC_TYPE_ID_DEATH_CERTIFICATE,
    title: "Please upload a copy of the death certificate",
    subtitle: "The death certificate serves as legal evidence and proof of death for legal purposes when interacting with institutions to access, transfer, or settle existing accounts, benefits and other estate assets."
  },
  [EXECUTOR_VERIFICATION_STEP_LETTERS_OR_AGREEMENT.toString()]: {
    image: "/static/images/Letters_Sample.svg",
    documentName: "Letters Testamentary (or similar)",
    documentTypeId: DOC_TYPE_ID_LETTERS_TESTAMENTARY,
    title: "We’re almost there! Now all we need is a copy of your “Letters” or Executor Agreement received from the Probate Court",
    subtitle: "These are often referred to as “Letters,” or the documents someone applies for to become the legal administrator of an estate."
  },
  [EXECUTOR_VERIFICATION_STEP_PHOTO_VERIFIATION_ID.toString()]: {
    image: "/static/images/Selfie_Sample.svg",
    documentName: "Photo Verification",
    documentTypeId: DOC_TYPE_ID_PHOTO_VERIFICATION,
    title: "It’s time for your close-up! Please upload a clear photo of yourself.",
    subtitle: "We know it’s going to be hard to choose between all your great pictures—think Passport or Driver’s License Photo guidelines, clear and centered."
  },
}

export const UploadFileScreen = (props: { variant: ExecutorVerificationSteps }): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { docs, loadingDocs, setDocs } = useFileCabinetContext()
  const { verificationStepId, getStepStatusId, setStepStatusId, setVerificationStepId } = useContext(ExecutorVerificationContext);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<FileList | undefined>();
  const [errors, setErrors] = React.useState<string[]>([]);
  const [progress, setProgress] = React.useState<number | undefined>();
  const [previewImage, setPreviewImage] = React.useState<string>(stepInfo[props.variant.toString()]?.image || "");
  const [previewImageKey, setPreviewImageKey] = React.useState<string>("initialKey");
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  useEffect(() => {
    setVerificationStepId(props.variant)
  }, [props.variant])

  const handleFileUpload = async (uploadedFiles: FileList): Promise<void> => {
    if (!activeEstate?.id || !user?.id) {
      setErrors(["We were unable to create the file in our system, if the problem persists please log out and back in"])
      return
    }

    const documentName = stepInfo[props.variant.toString()]?.documentName
    if (!documentName) {
      console.error("Unknown documentName ")
      return
    }
    const documentTypeId = stepInfo[props.variant.toString()]?.documentTypeId
    if (!documentTypeId) {
      console.error("Unknown documentTypeId ")
      return
    }

    setUploadedFiles(uploadedFiles);
    setIsSubmitting(true);
    setProgress(undefined);
    const uploadFile = uploadedFiles?.item(0) || undefined;
    const result = await documentsAPI.saveDocument(
      activeEstate?.id,
      user?.id,
      documentName,
      undefined,
      documentTypeId,
      uploadFile,
      undefined,
      "",
      undefined,
      (percent) => setProgress(percent), // callback to display progress,
      true
    );
    if (!result) {
      setErrors(["Sorry, file upload failed.  Please try again."]);
    }

    if (uploadFile && result && result?.version?.thumbnailS3Key) {
      setDocs([...docs, result])
    }

    setIsSubmitting(false);
  };

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    setIsSubmitting(true)

    try {
      if (getStepStatusId(verificationStepId) !== EXECUTOR_VERIFICATION_SUBMITTED_ID) {
        await setStepStatusId(verificationStepId, EXECUTOR_VERIFICATION_SUBMITTED_ID)
      }
    } catch (error) {
      console.error("issue updating step, " + (error as any)?.message)
      setIsSubmitting(false)
      return false
    }

    setIsSubmitting(false)

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const handleBack = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.back, navigate);
    return true;
  };


  useEffect(() => {
    // Preview image should be most recent doc, if any
    if (!loadingDocs && docs && docs.length > 0) {
      const doc = findMostRecentDoc(docs, stepInfo[props.variant.toString()]?.documentTypeId)
      if (!doc?.id || !doc?.version?.id) {
        return
      }

      if (!activeEstate?.id || !user?.id) {
        return
      }

      getDocumentContents(
        activeEstate.id,
        user.id,
        doc.id,
        doc.version.id,
        undefined,
        false,
        0,
        false,
      ).then(contents => {
        if (contents) {
          setPreviewImage(contents)
          setPreviewImageKey(contents)
        } else {
          setPreviewImage("")
          setPreviewImageKey("")
        }
      })
    }
  }, [loadingDocs, docs])


  if (!currentScreen) {
    return <></>;
  }


  return (
    <FlowScreenWrapper
      title={stepInfo[props.variant.toString()]?.title || ""}
      subTitle={stepInfo[props.variant.toString()]?.subtitle || ""}
    >
      <div>
        {(loadingDocs) && <Loading />}
        {(!loadingDocs) && <RenderFileUploadBox
          uploadedFiles={uploadedFiles}
          setUploadedFiles={handleFileUpload}
          setErrors={setErrors}
          previewImage={<img src={previewImage} style={{ maxWidth: "100%", minHeight: "220px", maxHeight: "300px" }} />}
          key={previewImageKey}
        />}

        {(!errors || errors.length === 0) && progress && progress < 100 && (
          <Box sx={{ width: "100%" }} key={"newfileprogressbox"}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="secondary"
              sx={{
                height: 16,
                mt: 2,
                mb: 1,
              }}
            />
          </Box>
        )}
        {(!errors || errors.length === 0) && progress === 100 && (
          <Grid
            container
            alignContent={"center"}
            alignItems={"center"}
            justifyContent={"center"}
            style={{ width: "100%" }}
          >
            <Grid
              item
              alignContent={"center"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Typography
                variant={"body1"}
                component={"p"}
                color="secondary"
                style={{ marginRight: "10px", fontSize: "14pt" }}
              >
                Upload successful
              </Typography>
            </Grid>
            <Grid item>
              <CheckCircle fontSize="large" color="secondary" />
            </Grid>
          </Grid>
        )}
        {errors && errors.length > 0 && (
          <Typography variant={"body1"} color={"error"}>
            {errors.map((e) => (
              <>
                {e}
                <br />
              </>
            ))}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          }}
        >
          <Button
            color={"secondary"}
            variant="contained"
            disabled={isSubmitting}
            sx={{ minWidth: 180 }}
            onClick={async () => {
              setIsSubmitting(true);
              handleComplete();
              setIsSubmitting(false);
            }}
          >
            Continue
          </Button>
          <IconButton
            color={"secondary"}
            disabled={isSubmitting}
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon color={"secondary"} />
          </IconButton>
        </Box>
      </div>
    </FlowScreenWrapper>
  );
};
